<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import { Chart, registerables } from 'chart.js';
import { BarChart, useBarChart } from 'vue-chart-3';
import { computed } from "vue";

const props = defineProps({
    year: String,
    user: Object,
    todaysBookingsCount: Number,
    todaysPendingBookingsCount: Number,
    todaysConfirmedBookingsCount: Number,
    todaysCancelledBookingsCount: Number,
    lastYearBookingsCount: Number,
    lastYearRoomNights: Number,
    currentYearBookingsCount: Number,
    currentYearRoomNights: Number,
    todaysArrivals: Number,
    todaysDepartures: Number,
    tomorrowArrivals: Number,
    tomorrowDepartures: Number,
    currentYearJanMarBookingsCount: Number,
    firstQuarterTotalRoomNights: Number,
    currentYearAprJunBookingsCount: Number,
    secondQuarterTotalRoomNights: Number,
    currentYearJulSepBookingsCount: Number,
    thirdQuarterTotalRoomNights: Number,
    currentYearOctDecBookingsCount: Number,
    fourthQuarterTotalRoomNights: Number,
    lastYearTotalInvoiceRevenue: Number,
    currentYearTotalInvoiceRevenue: Number,
    lastYearMonthlyRevenue: Array,
    currentYearMonthlyRevenue: Array,
});

Chart.register(...registerables);

const chartData = computed(() => {
    return {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: [
            {
                label: 'Year ' + lastYear(),
                data: props.lastYearMonthlyRevenue,
                backgroundColor: '#c5e1a5',
                borderColor: '#c5e1a5',
                borderWidth: 1,
            },
            {
                label: 'Year ' + currentYear(),
                data: props.currentYearMonthlyRevenue,
                backgroundColor: '#ef9a9a',
                borderColor: '#ef9a9a',
                borderWidth: 1,
            },
        ],
    };
});

const { barChartProps } = useBarChart({
    chartData,
    options: {
        scales: {
            y: {
                ticks: {
                    callback: function (value, index, values) {
                        return '$' + value;
                    },
                },
            },
        },
    },
});

//function to check last year only
function lastYear() {
    var d = new Date();
    var n = d.getFullYear();
    return n - 1;
}

//function to check current year only
function currentYear() {
    var d = new Date();
    var n = d.getFullYear();
    return n;
}

function calculatePercentageDifference(firstNumber, secondNumber) {
    // Calculate the percentage difference between the two numbers
    const difference = secondNumber - firstNumber;
    const percentageDifference = (difference / firstNumber) * 100;

    // Return the result as a string with two decimal places
    return percentageDifference.toFixed(2) + '%';
}

function formatPrice(value) {
    let val = (value / 1).toFixed(2).replace('.', '.');
    return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

</script>

<template>
    <AppLayout title="Dashboard" :year=year :user=user>
        <!-- Content -->
        <div class="container-fluid flex-grow-1 container-p-y">
            <div class="row">
                <div class="col-md-3 col-lg-3 mb-4"
                    v-if="$page.props.user.permissions.includes('show-todays-bookings-widget')">
                    <div class="card">
                        <div class="card-header d-flex align-items-center justify-content-between">
                            <h5 class="card-title m-0 me-2 text-primary">Today's Bookings</h5>
                        </div>
                        <div class="card-body">
                            <div class="p-0 m-0">
                                <table class="table table-sm table-centered mb-0 font-14">
                                    <tbody>
                                        <tr>
                                            <td>Total Bookings</td>
                                            <td></td>
                                            <td><i class="tf-icons bx bx-book"></i>
                                                {{ todaysBookingsCount }}</td>
                                        </tr>
                                        <tr>
                                            <td class="text-info">Confirmed</td>
                                            <td></td>
                                            <td><i class="tf-icons bx bx-book text-info"></i>
                                                {{ todaysConfirmedBookingsCount }}</td>
                                        </tr>
                                        <tr>
                                            <td class="text-warning">Pending</td>
                                            <td></td>
                                            <td><i class="tf-icons bx bx-book text-warning"></i>
                                                {{ todaysPendingBookingsCount }}</td>
                                        </tr>
                                        <tr>
                                            <td class="text-danger">Cancelled</td>
                                            <td></td>
                                            <td><i class="tf-icons bx bx-book text-danger"></i>
                                                {{ todaysCancelledBookingsCount }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-md-3 col-lg-3 mb-4"
                    v-if="$page.props.user.permissions.includes('show-lastyear-bookings-widget')">
                    <div class="card">
                        <div class="card-header d-flex align-items-center justify-content-between">
                            <h5 class="card-title m-0 me-2 text-primary">Last Year ({{ lastYear() }})</h5>
                        </div>
                        <div class="card-body">
                            <div class="p-0 m-0">
                                <table class="table table-sm table-centered mb-0 font-14">
                                    <tbody>
                                        <tr>
                                            <td>Total Bookings</td>
                                            <td></td>
                                            <td><i class="tf-icons bx bx-book text-info"></i>
                                                {{ parseInt(lastYearBookingsCount) }}</td>
                                        </tr>
                                        <tr>
                                            <td class="text-info">Total Room Nights</td>
                                            <td></td>
                                            <td><i class="tf-icons bx bx-hotel text-danger"></i>
                                                {{ lastYearRoomNights }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-md-3 col-lg-3 mb-4"
                    v-if="$page.props.user.permissions.includes('show-currentyear-bookings-widget')">
                    <div class="card">
                        <div class="card-header d-flex align-items-center justify-content-between">
                            <h5 class="card-title m-0 me-2 text-primary">Current Year ({{ currentYear() }})</h5>
                        </div>
                        <div class="card-body">
                            <div class="p-0 m-0">
                                <table class="table table-sm table-centered mb-0 font-14">
                                    <tbody>
                                        <tr>
                                            <td>Total Bookings</td>
                                            <td></td>
                                            <td><i class="tf-icons bx bx-book text-info"></i>
                                                {{ parseInt(currentYearBookingsCount) }}</td>
                                        </tr>
                                        <tr>
                                            <td class="text-info">Total Room Nights</td>
                                            <td></td>
                                            <td><i class="tf-icons bx bx-hotel text-danger"></i>
                                                {{ currentYearRoomNights }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-md-3 col-lg-3 mb-4" v-if="$page.props.user.permissions.includes('show-arrvdepart-widget')">
                    <div class="card">
                        <div class="card-header d-flex align-items-center justify-content-between">
                            <h5 class="card-title m-0 me-2 text-primary">Arrivals / Departures</h5>
                        </div>
                        <div class="card-body">
                            <div class="p-0 m-0">
                                <table class="table table-sm table-centered mb-0 font-14">
                                    <tbody>
                                        <tr>
                                            <td>Today</td>
                                            <td><i class="bx bxs-plane-land text-info"></i> {{ todaysArrivals }}</td>
                                            <td><i class="bx bxs-plane-take-off text-danger"></i> {{ todaysDepartures }}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="text-info">Tomorrow</td>
                                            <td><i class="bx bxs-plane-land text-info"></i> {{ tomorrowArrivals }}</td>
                                            <td><i class="bx bxs-plane-take-off text-danger"></i> {{ tomorrowDepartures }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-md-12 col-lg-4"
                    v-if="$page.props.user.permissions.includes('show-quarterly-bookings-widget')">
                    <div class="row">
                        <div class="col-lg-6 col-md-3 col-6 mb-4">
                            <div class="card">
                                <div class="card-body">
                                    <div class="card-title d-flex align-items-start justify-content-between">
                                        <h6 class="card-title mb-0">
                                            Jan - Mar ({{ currentYear() }})</h6>
                                    </div>
                                    <table class="table table-sm table-centered mb-2 font-14">
                                        <tbody>
                                            <tr>
                                                <td>Bookings</td>
                                                <td></td>
                                                <td><i class="tf-icons bx bx-book text-info"></i>
                                                    {{ parseInt(currentYearJanMarBookingsCount) }}</td>
                                            </tr>
                                            <tr>
                                                <td class="text-info">Nights</td>
                                                <td></td>
                                                <td><i class="tf-icons bx bx-hotel text-danger"></i>
                                                    {{ parseInt(firstQuarterTotalRoomNights) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <small class="text-success fw-semibold">
                                        .</small>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg-6 col-md-3 col-6 mb-4">
                            <div class="card">
                                <div class="card-body">
                                    <div class="card-title d-flex align-items-start justify-content-between">
                                        <h6 class="card-title mb-0">Apr - Jun ({{ currentYear() }})</h6>
                                    </div>
                                    <table class="table table-sm table-centered mb-2 font-14">
                                        <tbody>
                                            <tr>
                                                <td>Bookings</td>
                                                <td></td>
                                                <td><i class="tf-icons bx bx-book text-info"></i>
                                                    {{ parseInt(currentYearAprJunBookingsCount) }}</td>
                                            </tr>
                                            <tr>
                                                <td class="text-info">Nights</td>
                                                <td></td>
                                                <td><i class="tf-icons bx bx-hotel text-danger"></i>
                                                    {{ parseInt(secondQuarterTotalRoomNights) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div>
                                        <span
                                            v-if="parseInt(currentYearAprJunBookingsCount) < parseInt(currentYearJanMarBookingsCount)">
                                            <small class="fw-semibold text-danger"><i class="bx bx-down-arrow-alt"></i>
                                                {{ calculatePercentageDifference(parseInt(currentYearJanMarBookingsCount),
                                                    parseInt(currentYearAprJunBookingsCount)) }}
                                            </small>
                                        </span>
                                        <span v-else>
                                            <small class="fw-semibold text-success"><i class="bx bx-up-arrow-alt"></i>
                                                {{ calculatePercentageDifference(parseInt(currentYearJanMarBookingsCount),
                                                    parseInt(currentYearAprJunBookingsCount)) }}
                                            </small>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg-6 col-md-3 col-6 mb-4">
                            <div class="card">
                                <div class="card-body">
                                    <div class="card-title d-flex align-items-start justify-content-between">
                                        <h6 class="card-title mb-0">Jul - Sep ({{ currentYear() }})</h6>
                                    </div>
                                    <table class="table table-sm table-centered mb-2 font-14">
                                        <tbody>
                                            <tr>
                                                <td>Bookings</td>
                                                <td></td>
                                                <td><i class="tf-icons bx bx-book text-info"></i>
                                                    {{ parseInt(currentYearJulSepBookingsCount) }}</td>
                                            </tr>
                                            <tr>
                                                <td class="text-info">Nights</td>
                                                <td></td>
                                                <td><i class="tf-icons bx bx-hotel text-danger"></i>
                                                    {{ parseInt(thirdQuarterTotalRoomNights) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div>
                                        <span
                                            v-if="parseInt(currentYearJulSepBookingsCount) < parseInt(currentYearAprJunBookingsCount)">
                                            <small class="fw-semibold text-danger"><i class="bx bx-down-arrow-alt"></i>
                                                {{ calculatePercentageDifference(parseInt(currentYearAprJunBookingsCount),
                                                    parseInt(currentYearJulSepBookingsCount)) }}
                                            </small>
                                        </span>
                                        <span v-else>
                                            <small class="fw-semibold text-success"><i class="bx bx-up-arrow-alt"></i>
                                                {{ calculatePercentageDifference(parseInt(currentYearAprJunBookingsCount),
                                                    parseInt(currentYearJulSepBookingsCount)) }}
                                            </small>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg-6 col-md-3 col-6 mb-4">
                            <div class="card">
                                <div class="card-body">
                                    <div class="card-title d-flex align-items-start justify-content-between">
                                        <h6 class="card-title mb-0">Oct - Dec ({{ currentYear() }})</h6>
                                    </div>
                                    <table class="table table-sm table-centered mb-2 font-14">
                                        <tbody>
                                            <tr>
                                                <td>Bookings</td>
                                                <td></td>
                                                <td><i class="tf-icons bx bx-book text-info"></i>
                                                    {{ parseInt(currentYearOctDecBookingsCount) }}</td>
                                            </tr>
                                            <tr>
                                                <td class="text-info">Nights</td>
                                                <td></td>
                                                <td><i class="tf-icons bx bx-hotel text-danger"></i>
                                                    {{ parseInt(fourthQuarterTotalRoomNights) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div>
                                        <span
                                            v-if="parseInt(currentYearOctDecBookingsCount) < parseInt(currentYearJulSepBookingsCount)">
                                            <small class="fw-semibold text-danger"><i class="bx bx-down-arrow-alt"></i>
                                                {{ calculatePercentageDifference(parseInt(currentYearJulSepBookingsCount),
                                                    parseInt(currentYearOctDecBookingsCount)) }}
                                            </small>
                                        </span>
                                        <span v-else>
                                            <small class="fw-semibold text-success"><i class="bx bx-up-arrow-alt"></i>
                                                {{ calculatePercentageDifference(parseInt(currentYearJulSepBookingsCount),
                                                    parseInt(currentYearOctDecBookingsCount)) }}
                                            </small>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-md-12 col-lg-8 mb-4" v-if="$page.props.user.permissions.includes('show-revenue-widget')">
                    <div class="card">
                        <div class="row row-bordered g-0">
                            <div class="col-md-9">
                                <div class="card-header">
                                    <h5 class="card-title mb-0">Total Revenue</h5>
                                    <small class="card-subtitle">Yearly report overview</small>
                                </div>
                                <div class="card-body" style="position: relative;">
                                    <BarChart v-bind="barChartProps" />

                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="card-header d-flex justify-content-between">
                                    <div>
                                        <h5 class="card-title mb-0">Report</h5>
                                    </div>
                                </div>
                                <div class="card-body">
                                    <div class="report-list">
                                        <div class="report-list-item rounded-2 mb-3">
                                            <div class="d-flex align-items-start">
                                                <div class="report-list-icon shadow-sm me-2">
                                                    <img src="/ui/svg/icons/wallet-icon.svg" width="22" height="22"
                                                        alt="Paypal">
                                                </div>
                                                <div
                                                    class="d-flex justify-content-between align-items-end w-100 flex-wrap gap-2">
                                                    <div class="d-flex flex-column">
                                                        <span>Last Year Total</span>
                                                        <h5 class="mb-0">${{ formatPrice(lastYearTotalInvoiceRevenue) }}
                                                        </h5>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="report-list-item rounded-2 mb-3">
                                            <div class="d-flex align-items-start">
                                                <div class="report-list-icon shadow-sm me-2">
                                                    <img src="/ui/svg/icons/wallet-icon.svg" width="22" height="22"
                                                        alt="Shopping Bag">
                                                </div>
                                                <div
                                                    class="d-flex justify-content-between align-items-end w-100 flex-wrap gap-2">
                                                    <div class="d-flex flex-column">
                                                        <span>Current Year Total</span>
                                                        <h5 class="mb-0">${{ formatPrice(currentYearTotalInvoiceRevenue) }}
                                                        </h5>
                                                    </div>
                                                    <span
                                                        v-if="currentYearTotalInvoiceRevenue < lastYearTotalInvoiceRevenue">
                                                        <small class="fw-semibold text-danger"><i
                                                                class="bx bx-down-arrow-alt"></i>
                                                            {{ calculatePercentageDifference(lastYearTotalInvoiceRevenue,
                                                                currentYearTotalInvoiceRevenue) }}
                                                        </small>
                                                    </span>
                                                    <span v-else>
                                                        <small class="fw-semibold text-success"><i
                                                                class="bx bx-up-arrow-alt"></i>
                                                            {{ calculatePercentageDifference(lastYearTotalInvoiceRevenue,
                                                                currentYearTotalInvoiceRevenue) }}
                                                        </small>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>